import type { LandingPageTemplateResponse } from "@/types/landing";
import type { OfferPageTemplateResponse } from "@/types/offers";
import type { Site } from "@/types/sites";
import { getImageUrl } from "../utils";
import { type CmsMedia } from "@/types/strapi";

const getImageUrlOrNone = (
    url: string | undefined | null,
    urlsOnly = false
): string => {
    if (!url) {
        return "none";
    } else if (urlsOnly) {
        return getImageUrl(url);
    } else {
        // https://stackoverflow.com/a/54133135/498463
        return `url(${getImageUrl(url)})`;
    }
};

export type MediaSrcAndMime = {
    src: string;
    mime: string | undefined;
    preload: boolean;
};

const getBackgrounds = (
    desktop: CmsMedia | undefined,
    mobile: CmsMedia | undefined,
    mobileIsOptional = false,
    urlsOnly = false
): MediaSrcAndMime[] => {
    const desktopBackground = getImageUrlOrNone(
        desktop?.data?.attributes?.url,
        urlsOnly
    );
    const desktopBackgroundMime = desktop?.data?.attributes?.mime;

    let mobileBackground = getImageUrlOrNone(
        mobile?.data?.attributes?.url,
        urlsOnly
    );
    let mobileBackgroundMime = mobile?.data?.attributes?.mime;

    // If there is no mobile background image, just use the desktop background image
    // as requested by Nermin 24/4/2023
    if (mobileIsOptional && mobileBackground === "none") {
        mobileBackground = desktopBackground;
        mobileBackgroundMime = desktopBackgroundMime;
    }

    return [
        {
            src: desktopBackground,
            mime: desktopBackgroundMime,
            preload: mobile === undefined, // desktop css is usually in a mdeia query, so we don't need to preload it
        },
        {
            src: mobileBackground,
            mime: mobileBackgroundMime,
            preload: true,
        },
    ];
};

export const getSiteBackgrounds = (
    site: Site,
    urlsOnly = false
): MediaSrcAndMime[] => {
    return getBackgrounds(
        site?.attributes?.background,
        site?.attributes?.backgroundMobile,
        true,
        urlsOnly
    );
};

export const getOfferPageBackgrounds = (
    offerPage: OfferPageTemplateResponse,
    urlsOnly = false
): MediaSrcAndMime[] => {
    return getBackgrounds(
        offerPage?.data?.attributes?.headerBackground,
        undefined, // this one doesn't have mobile/desktop varients, it is the same image for both.,
        false,
        urlsOnly
    );
};

export const getLandingPageBackgrounds = (
    landingPage: LandingPageTemplateResponse,
    urlsOnly = false
): MediaSrcAndMime[] => {
    return getBackgrounds(
        landingPage?.data?.attributes?.backgroundImageDesktop,
        landingPage?.data?.attributes?.backgroundImageMobile,
        false,
        urlsOnly
    );
};
